import { useState, useEffect, useRef } from "react";
import { Search, X } from "lucide-react";
import { fetchAllProducts, fallbackImage } from "@/lib/catalog";
import { useNavigate } from "@tanstack/react-router";
import * as Dialog from "@radix-ui/react-dialog";

export function ProductSearch({ children }: { children?: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [products, setProducts] = useState<any[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      fetchAllProducts().then(setProducts);
    } else {
      setQuery("");
    }
  }, [open]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (open) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const target = e.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) {
        return;
      }

      if (e.key.length === 1 && /[a-zA-Z0-9]/.test(e.key)) {
        setOpen(true);
        setQuery(e.key);
        e.preventDefault();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  const filtered =
    query.length > 0
      ? products.filter((p) => {
          const q = query.toLowerCase();
          return (
            p.name.toLowerCase().includes(q) ||
            p.handle.toLowerCase().includes(q) ||
            (p.categories?.name || "").toLowerCase().includes(q) ||
            (p.specs?.type || "").toLowerCase().includes(q) ||
            (p.specs?.cultivation || "").toLowerCase().includes(q) ||
            (p.specs?.tea_type || "").toLowerCase().includes(q)
          );
        })
      : [];

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        {children || (
          <button
            type="button"
            className="relative inline-flex items-center justify-center rounded-full p-2 text-foreground/80 hover:bg-secondary hover:text-primary transition-colors"
            aria-label="Search products"
            title="Type anywhere to search"
          >
            <Search className="h-5 w-5" />
          </button>
        )}
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-16 z-50 w-full max-w-2xl -translate-x-1/2 bg-card border border-border shadow-2xl sm:rounded-lg overflow-hidden flex flex-col max-h-[80vh] outline-none sm:top-1/4">
          <div className="flex items-center border-b border-border px-4 py-3">
            <Search className="h-5 w-5 text-muted-foreground mr-3" />
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search products..."
              className="flex-1 bg-transparent outline-none text-lg placeholder:text-muted-foreground"
            />
            <Dialog.Close asChild>
              <button className="text-muted-foreground hover:text-foreground p-1">
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          <div className="overflow-y-auto flex-1 p-2">
            {query.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">
                Type to search for products...
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">
                No products found for "{query}"
              </div>
            ) : (
              <ul className="space-y-1">
                {filtered.map((p) => (
                  <li key={p.id}>
                    <button
                      onClick={() => {
                        setOpen(false);
                        navigate({ to: `/products/${p.handle}` });
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-muted rounded-md flex items-center gap-4 transition-colors"
                    >
                      <img
                        src={p.image_url || fallbackImage(p.categories?.handle)}
                        alt={p.name}
                        width={40}
                        height={40}
                        className="w-10 h-10 object-cover rounded"
                      />
                      <div>
                        <div className="font-serif">{p.name}</div>
                        {p.categories && (
                          <div className="text-xs text-muted-foreground uppercase tracking-widest mt-0.5">
                            {p.categories.name}
                          </div>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
